/**
 * ReportProgressTimeline — Apple-style vertical process timeline for Life
 * Report generation. Renders a stepped list with:
 *   • Completed stage → filled purple circle + check
 *   • Active stage    → pulsing ring + current label
 *   • Pending stage   → thin outline circle
 *
 * The 7 user-visible stages map 1:1 to the backend keys emitted by
 * routes_report.py (via /report/progress) and the normalised keys in
 * routes_report_jobs.py (via /reports/jobs/{id}).
 *
 * Driven entirely by `progress.stage` (legacy) or `job.status + stages_completed`
 * (new async job) — no fabricated timers.
 */
import React from "react";
import { Check } from "lucide-react";

export const REPORT_STAGES = [
  { key: "understanding",     label: "Understanding Your Birth Details",   legacy: ["validating"],              job: ["VALIDATING", "CREDITS_RESERVED"] },
  { key: "chart",             label: "Calculating Your Vedic Birth Chart", legacy: ["chart"],                   job: ["CALCULATING_CHART"] },
  { key: "patterns",          label: "Analyzing Your Life Patterns",       legacy: ["transits"],                job: ["ANALYZING_LIFE_EVENTS"] },
  { key: "vedic_timing",      label: "Vedic Timing",                       legacy: ["vedic_timing"],            job: ["VEDIC_TIMING"] },
  { key: "wisdom",            label: "KALKI Wisdom",                       legacy: ["interpreting", "wisdom", "questions"], job: ["KALKI_WISDOM", "CALCULATING_PREDICTIONS"] },
  { key: "report",            label: "Preparing Your Life Report",         legacy: ["composing", "retrying"],   job: ["GENERATING_REPORT"] },
  { key: "pdf",               label: "Finalizing Your PDF",                legacy: ["rendering"],               job: ["RENDERING_PDF"] },
];

function stageState(idx, stages, activeIndex) {
  if (activeIndex === -2) return "pending"; // not started
  if (activeIndex === -1) return "completed"; // done — all ticked
  if (idx < activeIndex) return "completed";
  if (idx === activeIndex) return "active";
  return "pending";
}

/**
 * Resolve which stage index is currently active.
 *
 * Priority order:
 *   1. `stagesCompleted` (from async job) — mark all contained stages done,
 *      next unmarked stage becomes active.
 *   2. `jobStatus` — look up by the stage mapping.
 *   3. `legacyStage` — the legacy /report/progress string.
 */
export function activeStageIndex({ jobStatus, legacyStage, stagesCompleted, done, failed }) {
  if (done) return -1;
  if (failed) return -1;
  if (Array.isArray(stagesCompleted) && stagesCompleted.length) {
    // Find the furthest stage whose job key is in stagesCompleted.
    let maxIdx = -1;
    REPORT_STAGES.forEach((s, i) => {
      if (s.job.some((k) => stagesCompleted.includes(k))) maxIdx = Math.max(maxIdx, i);
    });
    // Next pending stage is active unless we're already at the last stage.
    return Math.min(maxIdx + 1, REPORT_STAGES.length - 1);
  }
  if (jobStatus) {
    const idx = REPORT_STAGES.findIndex((s) => s.job.includes(jobStatus));
    if (idx !== -1) return idx;
  }
  if (legacyStage) {
    const idx = REPORT_STAGES.findIndex((s) => s.legacy.includes(legacyStage));
    if (idx !== -1) return idx;
  }
  return -2; // not started
}

export default function ReportProgressTimeline({
  jobStatus,          // e.g. "VEDIC_TIMING"
  legacyStage,        // e.g. "vedic_timing" from /report/progress
  stagesCompleted,    // array of JOB_STATES completed so far
  done = false,
  failed = false,
  title = "Your Life Report is Taking Shape",
  subtitle = "KALKI is weaving your calculations. This takes about two minutes.",
  // v0.6 Phase E — real backend-timestamp timer (seconds).
  elapsedSeconds = null,
  targetSeconds = 600,
}) {
  const active = activeStageIndex({ jobStatus, legacyStage, stagesCompleted, done, failed });
  const fmt = (s) => {
    const m = Math.floor((s || 0) / 60);
    const r = Math.max(0, Math.floor((s || 0) % 60));
    return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
  };
  // v3.0 P0 — Local countdown animation between authoritative server polls.
  // The parent passes elapsedSeconds from the backend's `created_at` timestamp
  // on each job poll (every 1.5s). We tick a local 1-second interval so the
  // user sees a smooth 10:00 → 09:59 → 09:58 countdown instead of a jumpy
  // every-1.5-seconds update, while the server value is still authoritative
  // after each poll.
  const [localElapsed, setLocalElapsed] = React.useState(elapsedSeconds || 0);
  React.useEffect(() => {
    if (elapsedSeconds == null) return;
    setLocalElapsed(elapsedSeconds);
  }, [elapsedSeconds]);
  React.useEffect(() => {
    if (done || failed || elapsedSeconds == null) return;
    const id = setInterval(() => setLocalElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [done, failed, elapsedSeconds]);

  const currentElapsed = elapsedSeconds == null ? null : localElapsed;
  const remaining = currentElapsed == null ? null : Math.max(0, targetSeconds - currentElapsed);
  const overBudget = currentElapsed != null && currentElapsed >= targetSeconds && !done && !failed;
  const timerLabel = remaining == null ? null : fmt(done ? 0 : remaining);

  return (
    <div
      className="k-timeline"
      data-testid="report-progress-timeline"
      role="status"
      aria-live="polite"
      style={{
        maxWidth: 460,
        margin: "0 auto",
        padding: "28px 24px",
        borderRadius: 20,
        background: "linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))",
        border: "1px solid rgba(207,168,255,0.18)",
        backdropFilter: "blur(12px)",
      }}
    >
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 24, fontWeight: 400, color: "#F6F1FF", letterSpacing: 0.3 }}>
          {title}
        </div>
        <div style={{ fontSize: 12, lineHeight: 1.6, color: "rgba(230,220,255,0.6)", marginTop: 8, maxWidth: 360, marginLeft: "auto", marginRight: "auto" }}>
          {subtitle}
        </div>
        {timerLabel && (
          <div
            data-testid="report-timer"
            style={{
              marginTop: 20,
              display: "flex", flexDirection: "column", alignItems: "center",
              padding: "14px 20px", borderRadius: 16,
              background: overBudget ? "rgba(255,70,110,0.14)" : "rgba(139,92,246,0.14)",
              border: `1px solid ${overBudget ? "rgba(255,70,110,0.32)" : "rgba(167,139,250,0.28)"}`,
              color: overBudget ? "#FFB4C2" : "#F6F1FF",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            <div style={{ fontSize: 10, letterSpacing: "0.3em", color: "rgba(230,220,255,0.6)", marginBottom: 4, textTransform: "uppercase" }}>
              {done ? "Generation Complete" : overBudget ? "Generation Delayed · Recovering" : "Time Remaining"}
            </div>
            <div style={{ fontSize: 36, fontWeight: 300, letterSpacing: "0.04em", lineHeight: 1, fontFamily: "'Cormorant Garamond', serif" }}>
              {timerLabel}
            </div>
            <div style={{ fontSize: 10, letterSpacing: "0.2em", color: "rgba(230,220,255,0.5)", marginTop: 4 }}>
              elapsed {fmt(currentElapsed)} / {fmt(targetSeconds)} target
            </div>
          </div>
        )}
      </div>

      <ol style={{ listStyle: "none", padding: 0, margin: 0, position: "relative" }}>
        {REPORT_STAGES.map((s, i) => {
          const state = stageState(i, REPORT_STAGES, active);
          const isLast = i === REPORT_STAGES.length - 1;
          const circleBg =
            state === "completed" ? "linear-gradient(135deg,#8B5CF6,#D946EF)" :
            state === "active"    ? "rgba(139,92,246,0.15)" : "transparent";
          const circleBorder =
            state === "completed" ? "transparent" :
            state === "active"    ? "#A78BFA" : "rgba(230,220,255,0.25)";
          const labelColor =
            state === "pending" ? "rgba(230,220,255,0.5)" :
            state === "active"  ? "#F6F1FF" : "rgba(230,220,255,0.82)";

          return (
            <li
              key={s.key}
              data-testid={`timeline-step-${s.key}`}
              data-state={state}
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "10px 0",
              }}
            >
              {/* Connector line */}
              {!isLast && (
                <span
                  aria-hidden
                  style={{
                    position: "absolute",
                    left: 11,
                    top: 32,
                    bottom: -10,
                    width: 1.5,
                    background: state === "completed"
                      ? "linear-gradient(180deg,#A78BFA,#D946EF 60%,rgba(230,220,255,0.25))"
                      : "rgba(230,220,255,0.14)",
                  }}
                />
              )}

              <span
                aria-hidden
                style={{
                  position: "relative",
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: circleBg,
                  border: state === "completed" ? "none" : `1.5px solid ${circleBorder}`,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  transition: "background 220ms ease, border-color 220ms ease",
                  boxShadow: state === "active" ? "0 0 0 6px rgba(139,92,246,0.15)" : "none",
                }}
              >
                {state === "completed" ? (
                  <Check size={14} color="#fff" strokeWidth={3} />
                ) : state === "active" ? (
                  <span style={{
                    width: 8, height: 8, borderRadius: "50%",
                    background: "#A78BFA",
                    animation: "k-pulse 1.4s ease-in-out infinite",
                  }} />
                ) : null}
              </span>
              <span style={{
                fontSize: 13.5,
                color: labelColor,
                fontWeight: state === "active" ? 500 : 400,
                letterSpacing: 0.1,
              }}>
                {s.label}
              </span>
            </li>
          );
        })}
      </ol>
      <style>{`
        @keyframes k-pulse {
          0%,100% { transform: scale(0.9); opacity: 0.8; }
          50%     { transform: scale(1.1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
